import React, { useRef, useState } from 'react';
import { Box, Typography, TextField, Button, Stack, CircularProgress } from '@mui/material';
import { Upload, Link } from 'lucide-react';

const navy = '#1e3a5f';

/**
 * ImageStep
 * העלאת תמונה / הזנת URL (ImageWidget)
 *
 * Props:
 *   formData        { imageUrl: string }
 *   onChange        (partial) => void
 *   onUploadImage   (file) => Promise<url>   — props-only, no Redux
 */
const ImageStep = ({ formData, onChange, onUploadImage }) => {
  const imageUrl = formData?.imageUrl || '';
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadImage) return;
    setUploading(true);
    setUploadError('');
    try {
      const url = await onUploadImage(file);
      onChange({ imageUrl: url });
    } catch (err) {
      setUploadError('שגיאה בהעלאת הקובץ — נסה שוב.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        בחירת תמונה
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.5 }}>
        הזן כתובת URL של תמונה קיימת, או העלה קובץ מהמחשב.
      </Typography>

      {/* URL input */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={0.75}>
          <Link size={15} color="#6b7280" />
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem' }}>
            כתובת URL
          </Typography>
        </Stack>
        <TextField
          size="small"
          fullWidth
          placeholder="https://example.com/image.jpg"
          value={imageUrl}
          onChange={(e) => onChange({ imageUrl: e.target.value })}
          sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } }}
        />
      </Box>

      {/* Upload button */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
          <Upload size={15} color="#6b7280" />
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem' }}>
            העלאת קובץ
          </Typography>
        </Stack>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <Button
          variant="outlined"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={14} /> : <Upload size={14} />}
          sx={{
            borderColor: '#e5e7eb', color: '#374151', textTransform: 'none',
            fontSize: '0.83rem', borderRadius: '8px',
            '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
          }}
        >
          {uploading ? 'מעלה...' : 'בחר קובץ'}
        </Button>

        {uploadError && (
          <Typography sx={{ fontSize: '0.78rem', color: '#ef4444', mt: 1 }}>
            {uploadError}
          </Typography>
        )}
      </Box>

      {/* Preview */}
      {imageUrl && (
        <Box sx={{ mt: 2.5, borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb', maxHeight: 200 }}>
          <img
            src={imageUrl}
            alt="תצוגה מקדימה"
            style={{ width: '100%', height: '100%', objectFit: 'contain', maxHeight: 200, display: 'block' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ImageStep;
